import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Target, Eye, XCircle, CheckCircle2, Quote } from "lucide-react";
import { IMAGES } from "../lib/media";

const PAIN_POINTS = {
  buyers: {
    problems: [
      "Highly dependent on local vendors for procurement",
      "Unorganised procurement methods",
      "Quality concerns",
      "Lack of trade finance & proper tracking",
      "Lack of trade assurance",
      "Unstructured documentation",
    ],
    solutions: [
      "One-stop streamlined procurement facility",
      "Access a nationwide network of verified suppliers",
      "Verified quality, certificate-backed",
      "Flexible payment modes with digital tracking",
      "Secured payments, verified suppliers",
      "Structured & digital documentation process",
    ],
  },
  suppliers: {
    problems: [
      "Limited market exposure",
      "Trust deficits & cashflow gaps",
      "Hours lost on unqualified leads",
      "Slow, paper-driven workflows",
      "Fragmented logistics & delayed deliveries",
      "Pricing on guesswork",
    ],
    solutions: [
      "Wider market reach, beyond local buyers",
      "Credibility through test-certificate verification",
      "Qualified, high-intent leads only",
      "Faster deal closure, less back and forth",
      "Real-time visibility for inventory",
      "Data-backed pricing power",
    ],
  },
};

const PURPOSE = [
  {
    id: "mission", label: "Our Mission", icon: Target, img: IMAGES.moltenSteel,
    headline: "Rebuild metal trade for the digital age.",
    body: "Transition the traditional metal trading ecosystem into a modern digital marketplace, removing the inefficiencies of legacy methods so every participant benefits, regardless of size or scale.",
    pillars: ["Transparency", "Speed", "Accessibility"],
  },
  {
    id: "vision", label: "Our Vision", icon: Eye, img: IMAGES.heroCoils,
    headline: "The platform metal markets trust first.",
    body: "Become the leading digital marketplace for metals and commodities, where every trade, from sourcing to negotiation to closing, happens seamlessly through IronByte.",
    pillars: ["Sourcing", "Negotiation", "Closing"],
  },
];

function PurposeCard({ card, index }) {
  const Icon = card.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.12 }}
      className="group relative rounded-2xl overflow-hidden border border-ib-steel/20 bg-ib-surface hover:border-ib-blue/50 transition-colors duration-300"
      data-testid={`${card.id}-card`}
    >
      <div className="relative h-52 sm:h-60 overflow-hidden">
        <img src={card.img} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <span className="absolute top-5 right-6 font-outfit font-extrabold text-6xl text-white/25 leading-none select-none">0{index + 1}</span>
        <div className="absolute bottom-5 left-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-ib-blue flex items-center justify-center shadow-lg shadow-ib-blue/30">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="font-jetbrains text-xs text-white tracking-widest uppercase">{card.label}</span>
        </div>
      </div>
      <div className="p-7">
        <h3 className="font-outfit font-extrabold text-2xl sm:text-3xl tracking-tight text-ib-text leading-tight">{card.headline}</h3>
        <p className="font-manrope text-sm md:text-base text-ib-text-secondary leading-relaxed mt-3">{card.body}</p>
        <div className="flex flex-wrap gap-2 mt-6">
          {card.pillars.map((p) => (
            <span key={p} className="px-3 py-1 rounded-full border border-ib-blue/30 bg-ib-blue/5 font-jetbrains text-[11px] text-ib-blue uppercase tracking-wider">{p}</span>
          ))}
        </div>
      </div>
      <div className="absolute bottom-0 left-0 h-1 w-0 bg-ib-blue group-hover:w-full transition-[width] duration-500" />
    </motion.article>
  );
}

const FOUNDER_QUOTES = [
  { name: "Aishwary Tandon", photo: "/team/aishwary.jpg", text: "Phone calls and guesswork built yesterday's metal market.", hl: "Data and trust will build tomorrow's" },
  { name: "Shubham Palit", photo: "/team/shubham-avatar.jpg", text: "Credit shouldn't be the reason a good project stalls. We bring", hl: "transparent trade finance to every metal order" },
  { name: "Sumit Singh", photo: "/team/sumit-avatar.jpg", text: "Every buyer deserves a supplier they can trust. Our network puts", hl: "verified mills and certified stock one click away" },
  { name: "Hemant Kumar Sinha", photo: "/team/hemant-avatar.jpg", text: "When pricing is clear and data is live, trade moves faster. IronByte gives every trader", hl: "the numbers big players always had" },
  { name: "Aakash Mozumdar", photo: "/team/aakash.jpg", text: "Steel is only as good as the day it arrives. We're building logistics where", hl: "every truck is tracked from plant to site" },
];

function QuoteDots({ active, onPick }) {
  return (
    <div className="flex gap-0.5 mt-5" data-testid="founder-quote-dots">
      {FOUNDER_QUOTES.map((q, i) => (
        <button key={q.name} type="button" aria-label={`Show quote from ${q.name}`} onClick={() => onPick(i)} data-testid={`founder-quote-dot-${i}`}
          className="group py-3 px-1 -my-3">
          <span className={`block h-1.5 rounded-full transition-[width,background-color] duration-300 ${i === active ? "w-8 bg-ib-blue" : "w-3 bg-ib-steel/40 group-hover:bg-ib-blue/50"}`} />
        </button>
      ))}
    </div>
  );
}

function FounderQuote() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = (dir) => setI((v) => (v + dir + FOUNDER_QUOTES.length) % FOUNDER_QUOTES.length);
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setI((v) => (v + 1) % FOUNDER_QUOTES.length), 6000);
    return () => clearInterval(id);
  }, [paused]);
  const q = FOUNDER_QUOTES[i];
  return (
    <motion.figure
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: 0.2 }}
      onPointerEnter={(e) => e.pointerType === "mouse" && setPaused(true)} onPointerLeave={(e) => e.pointerType === "mouse" && setPaused(false)}
      className="relative mb-20 rounded-2xl border border-ib-steel/20 bg-ib-surface overflow-hidden"
      data-testid="founder-quote"
    >
      <div className="absolute inset-y-0 left-0 w-1.5 bg-ib-blue" />
      <Quote className="absolute top-4 right-[38%] w-24 h-24 text-ib-blue/[0.07] rotate-180 hidden md:block" />
      <AnimatePresence mode="wait">
        <motion.div key={q.name} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.18}
          onDragStart={() => setPaused(true)}
          onDragEnd={(e, info) => {
            if (info.offset.x < -60 || info.velocity.x < -400) go(1);
            else if (info.offset.x > 60 || info.velocity.x > 400) go(-1);
          }}
          className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10 p-7 md:p-10 md:min-h-[200px] touch-pan-y cursor-grab active:cursor-grabbing select-none">
          <div className="flex-1">
            <blockquote className="font-outfit font-bold text-xl sm:text-2xl lg:text-[28px] leading-snug tracking-tight text-ib-text" data-testid="founder-quote-text">
              "{q.text} <span className="text-ib-blue">{q.hl}</span>."
            </blockquote>
            <QuoteDots active={i} onPick={setI} />
            <span className="md:hidden block mt-3 font-jetbrains text-[10px] text-ib-text-muted tracking-wider uppercase" data-testid="founder-quote-swipe-hint">
              ‹ swipe for more ›
            </span>
          </div>
          <figcaption className="flex items-center gap-4 md:pl-10 md:border-l md:border-ib-steel/20 flex-shrink-0 md:w-72">
            <img src={q.photo} alt={q.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-ib-blue/40" />
            <div>
              <div className="font-outfit font-bold text-ib-text" data-testid="founder-quote-name">{q.name}</div>
              <div className="font-jetbrains text-[11px] text-ib-text-muted uppercase tracking-wider">Co-founder, IronByte</div>
            </div>
          </figcaption>
        </motion.div>
      </AnimatePresence>
    </motion.figure>
  );
}

export function IronByteWay() {
  const [tab, setTab] = useState("buyers");
  const data = PAIN_POINTS[tab];

  return (
    <section id="about" data-testid="ironbyte-way-section" className="py-24 px-6 relative">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {PURPOSE.map((card, i) => <PurposeCard key={card.id} card={card} index={i} />)}
        </div>
        <FounderQuote />

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <span className="font-jetbrains text-xs text-ib-lime tracking-widest uppercase">The ironByte Way</span>
            <h2 className="font-outfit font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3 text-ib-text">
              From <span className="text-ib-blue">chaos</span> to control.
            </h2>
          </div>
          <div className="flex gap-2 p-1 rounded-full border border-ib-steel/30 bg-ib-surface/50 self-start md:self-auto">
            {["buyers", "suppliers"].map((t) => (
              <button
                key={t}
                data-testid={`way-tab-${t}`}
                onClick={() => setTab(t)}
                className={`px-5 py-2 rounded-full font-outfit text-sm capitalize transition-colors duration-200 ${
                  tab === t ? "bg-ib-blue text-white" : "text-ib-text-secondary hover:text-ib-text"
                }`}
              >
                For {t}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid={`way-content-${tab}`}>
          <div className="rounded-xl border border-ib-negative/20 bg-ib-surface/30 p-6">
            <h3 className="font-jetbrains text-xs text-ib-negative tracking-widest uppercase mb-5">The old way</h3>
            <ul className="space-y-3">
              {data.problems.map((p) => (
                <li key={p} className="flex items-start gap-3 font-manrope text-sm text-ib-text-secondary">
                  <XCircle className="w-4 h-4 text-ib-negative/70 mt-0.5 flex-shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-ib-lime/30 bg-ib-surface/50 p-6 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-48 h-48 bg-ib-lime/10 rounded-full blur-[60px] pointer-events-none" />
            <h3 className="font-jetbrains text-xs text-ib-lime tracking-widest uppercase mb-5">
              The <span className="font-unbounded font-black text-ib-text normal-case">Iron</span>
              <span className="text-ib-blue normal-case">Byte</span> way
            </h3>
            <ul className="space-y-3">
              {data.solutions.map((s) => (
                <li key={s} className="flex items-start gap-3 font-manrope text-sm text-ib-text">
                  <CheckCircle2 className="w-4 h-4 text-ib-lime mt-0.5 flex-shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
